import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { fetchProjectDropdownList } from "../../services/projectServices";
import {
  fetchSurveyDropdownList,
  generateReport,
  generateSurveyReport,
} from "../../services/surveyServices";
import { toast } from "react-toastify";
import { Download, Eye } from "lucide-react";
import {
  GenerateReportResponse,
  GenerateSurveyReportResponse,
  ReportRow,
} from "../../types/surveyTypes";
import { Spin } from "antd";

interface Project {
  id: number;
  name?: string;
}
interface Survey {
  id: number;
  name?: string;
}

const ReportManagement = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportUrl, setReportUrl] = useState<string>("");
  const roleName = localStorage.getItem("role_name");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const is_admin = roleName === "admin" ? 1 : 0;
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const result = await fetchProjectDropdownList();
        setProjects(result);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch projects");
      }
    };
    loadProjects();
  }, []);
  // load surveys when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setSurveys([]);
      setSelectedSurveyId("");
      return;
    }

    const loadSurveys = async () => {
      try {
        const result = await fetchSurveyDropdownList(
          Number(selectedProjectId),
          false,
          null
        );
        setSurveys(result);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch surveys");
      }
    };

    loadSurveys();
  }, [selectedProjectId]);

  const handleShowReport = async () => {
    setLoading(true);
    try {
      const res: GenerateSurveyReportResponse = await generateSurveyReport(
        selectedProjectId,
        selectedSurveyId,
        startDate || undefined,
        endDate || undefined,
        is_admin
      );
      if (res.status) {
        const reportDataFromAPI = res.data?.report_data || [];
        const fileUrlFromAPI = res.data?.file_url ?? ""; // handle null
        setReportData(reportDataFromAPI);
        setReportUrl(fileUrlFromAPI);
        toast.success("Report fetched successfully");
      } else {
        toast.error(res.message || "Failed to fetch report");
      }
    } catch (error: any) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  const handleExport = () => {
    if (reportUrl) {
      const link = document.createElement("a");
      link.href = reportUrl;
      link.setAttribute("download", ""); // let server suggest filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error("Export failed");
    }
  };

  const SelectDropdown = ({
    label,
    value,
    onChange,
    options,
    placeholder,
  }: {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: { id: number; name?: string }[];
    placeholder: string;
  }) => (
    <div className="w-full md:w-1/3">
      <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
            !value
              ? "border-red-500"
              : "border-stroke dark:border-form-strokedark"
          }`}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    </div>
  );
  const handleDownloadReport = async (report_id: string) => {
    try {
      setDownloadingId(report_id); // 👉 Start loader for this row

      const res: GenerateReportResponse = await generateReport(
        selectedProjectId,
        selectedSurveyId,
        report_id,
        is_admin
      );

      const fileUrl = res.file_url;

      if (!fileUrl) {
        toast.error("Report file not available");
        return;
      }

      window.open(fileUrl, "_blank");
      toast.success(res.message || "Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate report");
      console.error(error);
    } finally {
      setDownloadingId(null); // 👉 Stop loader
    }
  };

  return (
    <>
      <Breadcrumb pageName="Report Management" />
      <div className="bg-white dark:bg-boxdark p-6 rounded-md shadow-md max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end gap-4 flex-wrap">
          {/* Project Dropdown */}
          <SelectDropdown
            label="Select Project"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            options={projects}
            placeholder="-- Select Project --"
          />

          {/* Survey Dropdown */}
          <SelectDropdown
            label="Select Survey"
            value={selectedSurveyId}
            onChange={(e) => setSelectedSurveyId(e.target.value)}
            options={surveys}
            placeholder="-- Select Survey --"
          />

          {/* Start Date */}
          <div className="w-full md:w-1/4">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                // optional: reset end date if it's before new start date
                if (endDate && e.target.value > endDate) {
                  setEndDate("");
                }
              }}
              className="w-full rounded-lg border border-stroke dark:border-form-strokedark py-2 px-4 dark:bg-form-input dark:text-white"
            />
          </div>

          {/* End Date */}
          <div className="w-full md:w-1/4">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={!startDate} // disabled until start date is selected
              min={startDate || undefined} // prevent selecting before start date
              className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                !startDate
                  ? "bg-gray-100 cursor-not-allowed"
                  : "border-stroke dark:border-form-strokedark"
              }`}
            />
          </div>
          <div className="w-full md:w-auto flex gap-2">
            <button
              type="button"
              disabled={!selectedProjectId || !selectedSurveyId || loading}
              onClick={handleShowReport}
              className="bg-blue-600 text-white py-3 px-6 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
            >
              <Eye size={18} />
              Show Report
            </button>
            {reportUrl && reportData && reportData?.length > 0 && (
              <button
                type="button"
                disabled={loading}
                onClick={handleExport}
                className="bg-black text-white py-3 px-6 rounded-lg flex items-center gap-2 hover:bg-opacity-90 disabled:opacity-50"
              >
                <Download size={18} />
                Export Report
              </button>
            )}
          </div>
        </div>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Survey Name</th>
                <th className="py-4 px-4 text-left">User Name</th>
                <th className="py-4 px-4 text-left">Submitted At</th>
                <th className="py-4 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : reportData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              ) : (
                reportData.map((row, index) => {
                  const formattedDate = new Date(
                    row.submission_date
                  ).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  });
                  return (
                    <tr key={index}>
                      <td className="border-b py-4 px-4">{row.survey_name}</td>
                      <td className="border-b py-4 px-4">{row.user_name}</td>
                      <td className="border-b py-4 px-4">{formattedDate}</td>
                      <td className="border-b py-4 px-4">
                        <button
                          onClick={() => handleDownloadReport(row?.report_id)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Download"
                        >
                          {downloadingId === row?.report_id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                          ) : (
                            <Download size={18} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ReportManagement;
